import { EventSourcePolyfill } from 'event-source-polyfill'

import { API_ENDPOINTS } from 'config/ApiConfig'

// transfer_projects runs as a Celery task whose Redis key is
// task_stream:paic_transfer_projects_{task_id}; the events endpoint takes the part after "task_stream:"
const TRANSFER_STREAM_PREFIX = 'paic_transfer_projects_'

// Mirrors the PAIC run stream limits in PeoplePlusAIV2
const MAX_RETRIES = 5
const KEY_NOT_READY_RETRY_MS = 2000
const CONNECTION_RETRY_MS = 3000
const HEARTBEAT_TIMEOUT_MS = 60 * 60 * 1000

const toLower = (value) => String(value ?? '').toLowerCase()

// A failure event can also carry progress 100, so this is checked before completion
const isFailureEvent = ({ event, status, error }) =>
  Boolean(error) || toLower(event).includes('fail') || /fail|error/.test(toLower(status))

const isCompletedEvent = ({ event, status, progress }) =>
  Number(progress) >= 100 || /complete|success/.test(`${toLower(event)} ${toLower(status)}`)

// Readable reason from a failure event. `error` can arrive as a JSON-encoded
// string ('{"message": "..."}'), so its message is unwrapped when present.
export const getTransferErrorMessage = (taskEvent = {}) => {
  for (const value of [taskEvent.error, taskEvent.message, taskEvent.status]) {
    if (typeof value !== 'string' || !value.trim()) continue
    try {
      const parsed = JSON.parse(value)
      if (typeof parsed?.message === 'string' && parsed.message.trim()) return parsed.message
    } catch (_) {
      // plain-text reason
    }
    return value
  }
  return null
}

/**
 * Follows a transfer_projects task until it finishes.
 *
 * `promise` never rejects; it resolves with { outcome, payload } where outcome is
 *   'completed'   — terminal success event
 *   'failed'      — failure event (payload carries error/message/status)
 *   'unavailable' — stream never became ready or kept dropping after retries
 *   'cancelled'   — cancel() was called
 */
export const watchTransferTask = (taskId, { accessToken, onProgress } = {}) => {
  let cancel = () => {}

  const promise = new Promise((resolve) => {
    let eventSource = null
    let retryTimer = null
    let retryCount = 0
    let settled = false

    const closeStream = () => {
      clearTimeout(retryTimer)
      eventSource?.close()
      eventSource = null
    }

    const settle = (outcome, payload = {}) => {
      if (settled) return
      settled = true
      closeStream()
      resolve({ outcome, payload })
    }

    const retryOrGiveUp = (delay) => {
      closeStream()
      if (retryCount >= MAX_RETRIES) {
        settle('unavailable')
        return
      }
      retryCount += 1
      retryTimer = setTimeout(connect, delay)
    }

    const connect = () => {
      if (settled) return

      eventSource = new EventSourcePolyfill(`${API_ENDPOINTS.RAPPID_MQ_EVENTS}${TRANSFER_STREAM_PREFIX}${taskId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        heartbeatTimeout: HEARTBEAT_TIMEOUT_MS,
      })

      eventSource.onmessage = (event) => {
        if (settled) return

        let payload
        try {
          payload = JSON.parse(event.data)
        } catch (parseError) {
          console.error('Unable to parse transfer task event:', parseError)
          return
        }
        if (!payload || typeof payload !== 'object') return

        // Redis stream key not created yet — the task may not have started
        if (payload.event === 'key_does_not_exist') {
          retryOrGiveUp(KEY_NOT_READY_RETRY_MS)
          return
        }

        // A live event means the connection is healthy again
        retryCount = 0

        if (isFailureEvent(payload)) {
          settle('failed', payload)
          return
        }
        if (isCompletedEvent(payload)) {
          settle('completed', payload)
          return
        }
        onProgress?.(payload)
      }

      eventSource.onerror = () => {
        if (!settled) retryOrGiveUp(CONNECTION_RETRY_MS)
      }
    }

    cancel = () => settle('cancelled')
    connect()
  })

  return { promise, cancel }
}
